import { el } from "../core/dom";
import { part } from "../appearance/parts";
import { applyCssVariables } from "../appearance/theme-vars";
import {
  CONFIRM_DIALOG_DETAIL_ID,
  CONFIRM_DIALOG_ID,
  NOTICE_FONT_ROOT_ATTRIBUTE,
} from "../constants";
import type { ConfirmAction, ConfirmDialogOptions, ConfirmDialogRefs, NoticeChromeCopy } from "../types";
import { noticeChromeCopy } from "./chromeCopy";

const ACTION_COPY: Record<
  ConfirmAction,
  { title: keyof NoticeChromeCopy; detail: keyof NoticeChromeCopy }
> = {
  "accept-all": { title: "confirmAcceptAllTitle", detail: "confirmAcceptAllDetail" },
  "accept-selected": {
    title: "confirmAcceptSelectedTitle",
    detail: "confirmAcceptSelectedDetail",
  },
  decline: { title: "confirmDeclineTitle", detail: "confirmDeclineDetail" },
};

const overlayStyle = {
  position: "fixed",
  inset: "0",
  top: "0",
  left: "0",
  right: "0",
  bottom: "0",
  backgroundColor: "rgba(16, 24, 40, 0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  zIndex: "2147483647",
};

const panelStyle = {
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  padding: "24px",
  maxWidth: "380px",
  width: "100%",
  boxShadow: "0 12px 32px rgba(16, 24, 40, 0.18)",
  boxSizing: "border-box",
};

const titleStyle = {
  margin: "0 0 8px",
  fontSize: "16px",
  lineHeight: "24px",
  fontWeight: "600",
  color: "#101828",
};

const detailStyle = {
  margin: "0 0 20px",
  fontSize: "14px",
  lineHeight: "20px",
  color: "#475467",
};

const actionsStyle = {
  display: "flex",
  gap: "12px",
  justifyContent: "flex-end",
};

const buttonStyle = {
  borderRadius: "8px",
  padding: "8px 20px",
  fontSize: "14px",
  lineHeight: "20px",
  cursor: "pointer",
  border: "1px solid #d0d5dd",
  backgroundColor: "#ffffff",
  color: "#344054",
};

const activeIn = (root: ShadowRoot | Document): Element | null =>
  root.activeElement ?? document.activeElement;

export function openConfirmDialog(
  options: ConfirmDialogOptions
): ConfirmDialogRefs {
  const { root, action, onConfirm, onCancel, confirmStyle, fontFamily, skin } =
    options;
  const copy = noticeChromeCopy(options.language);
  const opener = activeIn(root);
  let disposed = false;

  const dispose = () => {
    if (disposed) return;
    disposed = true;
    document.removeEventListener("keydown", onKeyDown, true);
    overlay.parentNode?.removeChild(overlay);
  };

  const cancel = () => {
    dispose();
    onCancel();
    if (opener instanceof HTMLElement) opener.focus();
  };

  const confirm = () => {
    dispose();
    onConfirm();
  };

  const cancelButton = el(
    "button",
    { type: "button", style: { ...buttonStyle, ...skin?.button }, onClick: cancel },
    copy.no
  ) as HTMLButtonElement;

  const confirmButton = el(
    "button",
    {
      type: "button",
      style: { ...buttonStyle, ...skin?.button, ...confirmStyle },
      onClick: confirm,
    },
    copy.yes
  ) as HTMLButtonElement;

  const panel = el(
    "div",
    {
      role: "dialog",
      ...part("confirm-dialog"),
      "aria-modal": "true",
      "aria-labelledby": CONFIRM_DIALOG_ID,
      "aria-describedby": CONFIRM_DIALOG_DETAIL_ID,
      style: { ...panelStyle, ...skin?.panel },
    },
    el(
      "p",
      { id: CONFIRM_DIALOG_ID, style: { ...titleStyle, ...skin?.title } },
      copy[ACTION_COPY[action].title]
    ),
    el(
      "p",
      { id: CONFIRM_DIALOG_DETAIL_ID, style: { ...detailStyle, ...skin?.detail } },
      copy[ACTION_COPY[action].detail]
    ),
    el("div", { style: actionsStyle }, cancelButton, confirmButton)
  );

  // The notice's root attributes, so its stylesheets and the admin's scoped
  // custom CSS reach the confirm step too.
  const overlay = el(
    "div",
    {
      ...options.rootAttributes,
      [NOTICE_FONT_ROOT_ATTRIBUTE]: "",
      style: { ...overlayStyle, ...skin?.overlay, fontFamily: fontFamily || "inherit" },
    },
    panel
  );
  if (options.rootVariables) applyCssVariables(overlay, options.rootVariables);

  function onKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancel();
      return;
    }
    if (event.key !== "Tab") return;
    event.stopPropagation();

    const active = activeIn(root);
    if (event.shiftKey && active === cancelButton) {
      event.preventDefault();
      confirmButton.focus();
      return;
    }
    if (!event.shiftKey && active === confirmButton) {
      event.preventDefault();
      cancelButton.focus();
      return;
    }
    if (active !== cancelButton && active !== confirmButton) {
      event.preventDefault();
      cancelButton.focus();
    }
  }

  document.addEventListener("keydown", onKeyDown, true);
  (root instanceof Document ? root.body : root).appendChild(overlay);
  cancelButton.focus();

  return { element: overlay, dispose };
}
